import React from 'react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const socialLinks = [
    {
      name: "LinkedIn",
      icon: "fa-linkedin",
      url: "https://www.linkedin.com/in/mimouni-sirine/",
      color: "hover:text-blue-600"
    },
    {
      name: "Email",
      icon: "fa-envelope", 
      url: "mailto:mimounisirine348@email.com",
      color: "hover:text-red-500"
    },
    {
      name: "Phone",
      icon: "fa-phone",
      url: "tel:+21658086751",
      color: "hover:text-green-500"
    }
  ];

  const quickLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Experience", href: "#experience" },
    { name: "Projects", href: "#projects" },
    { name: "Skills", href: "#skills" },
    { name: "Education", href: "#education" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden pt-16">
      {/* Decorative SVG divider */}
      <div className="absolute top-0 left-0 w-full -mt-1">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-24">
          <path fill="#18181b" d="M0,0 C480,100 960,0 1440,100 L1440,0 L0,0 Z" />
        </svg>
      </div>

      <div className="container-max relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12">
          {/* Profile & Social */}
          <div className="lg:col-span-2 flex flex-col justify-between">
            <div>
              <h3 className="text-3xl font-extrabold mb-4 tracking-tight flex items-center gap-2">
                <span className="inline-block bg-gradient-to-r from-primary-500 to-primary-700 text-transparent bg-clip-text">Sirine Mimouni</span>
                <span className="inline-block animate-bounce"><i className="fas fa-star text-yellow-400"></i></span>
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed border-l-4 border-primary-600 pl-4 italic">
                Data Scientist and Applied Mathematics student passionate about transforming 
                data into actionable insights. Specialized in deep learning, statistical analysis, 
                and building intelligent systems.
              </p>
            </div>
            <div className="flex space-x-4 mt-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target={social.url.startsWith('http') ? '_blank' : '_self'}
                  rel={social.url.startsWith('http') ? 'noopener noreferrer' : ''}
                  className={`w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-400 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 ${social.color}`}
                  aria-label={social.name}
                >
                  <i className={`${social.icon === 'fa-linkedin' ? 'fab' : 'fas'} ${social.icon} text-2xl`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xl font-bold mb-4 border-b-2 border-primary-600 inline-block pb-1">Quick Links</h4>
            <ul className="space-y-2 mt-4">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-300 font-medium"
                  >
                    <i className="fas fa-chevron-right mr-2 text-xs text-primary-400"></i>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xl font-bold mb-4 border-b-2 border-primary-600 inline-block pb-1">Contact Info</h4>
            <div className="space-y-4 mt-4">
              <div className="flex items-center text-gray-300">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600/20 mr-3">
                  <i className="fas fa-envelope text-primary-400"></i>
                </span>
                <a 
                  href="mailto:mimounisirine348@email.com"
                  className="hover:text-primary-400 transition-colors duration-300 font-medium"
                >
                  mimounisirine348@email.com
                </a>
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600/20 mr-3">
                  <i className="fas fa-phone text-primary-400"></i>
                </span>
                <a 
                  href="tel:+21658086751"
                  className="hover:text-primary-400 transition-colors duration-300 font-medium"
                >
                  +216 58 086 751
                </a>
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-600/20 mr-3">
                  <i className="fas fa-map-marker-alt text-primary-400"></i>
                </span>
                <span>Manouba, Tunisia</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-8">
          <div className="h-1 w-full bg-gradient-to-r from-primary-400 via-primary-600 to-primary-400 rounded-full opacity-40"></div>
        </div>

        {/* Bottom Row */}
        <div className="flex flex-col md:flex-row justify-between items-center py-6">
          <div className="text-gray-400 text-sm mb-4 md:mb-0 flex items-center gap-2">
            <i className="fas fa-copyright text-xs"></i>
            {currentYear} Sirine Mimouni. All rights reserved.
          </div>
          <div className="text-gray-400 text-sm flex items-center gap-2">
            <span>Designed and developed by</span>
            <i className="fas fa-heart text-red-500 mx-1 animate-pulse"></i>
            <a 
              href="https://www.mediagize.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="underline hover:text-primary-400 transition-colors duration-300"
            >
              Mediagize
            </a>.
          </div>
        </div>
      </div>

      {/* Decorative bottom SVG */}
      <div className="absolute bottom-0 left-0 w-full">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-16">
          <path fill="#18181b" d="M0,80 C480,0 960,80 1440,0 L1440,80 L0,80 Z" />
        </svg>
      </div>
    </footer>
  );
};

export default Footer;
